<script setup lang="ts">
import { ref, Ref, watch, onMounted, computed } from 'vue';
import axios from 'axios';

import { Media, MediaCollection, ParentModel, BulkMediaItem } from '@/types';
import FileUpload from '@/assets/FileUpload.vue';
import MediaForm from './MediaForm.vue';
import BulkUploadForm from './BulkUploadForm.vue';

const props = defineProps({
  parent: {
    type: Object,
    default: null,
  }
})

const emit = defineEmits(['created'])

// Collections (shared)
const collections: Ref<Array<MediaCollection>> = ref([]);
const collectionsLoaded = ref<boolean>(false);
const parent: Ref<ParentModel|null> = ref(props.parent);

// --- Single file mode (legacy, kept for backward compat if needed) ---
const file = ref<File | null>(null);
const media: Ref<Media> = ref({
    id: null,
    name: '',
    data: {
      alt: '',
      title: '',
      is_free_license: false,
    },
    collections: [],
    created_at: '',
    updated_at: '',
});

// --- Multi/Bulk file mode ---
const files = ref<File[]>([]);
const mediaItems: Ref<BulkMediaItem[]> = ref([]);
const selectedIndex = ref<number|null>(null);
const uploading = ref<boolean>(false);
const uploadProgress = ref<{ done: number; total: number }>({ done: 0, total: 0 });

// Use multi mode always now
const isMultiMode = computed(() => true);

const defaultCollectionId = ref<number|null>(null);

onMounted(async () => {
  try {
    const response = await axios.get(`/api/media/collections`)
    collections.value = response.data.media_collections

    const defaultCollection = parent.value?.type === 'icon'
      ? collections.value.find(c => c.name === 'Icons')
      : collections.value[0]

    if (defaultCollection) {
      defaultCollectionId.value = defaultCollection.id
      media.value.collections = [{ id: defaultCollection.id }]
    }
  } catch (error) {
    console.error(error)
  } finally {
    collectionsLoaded.value = true
  }
})

// Watch files array and sync mediaItems
watch(files, (newFiles) => {
  // Add new items for files that don't have a corresponding mediaItem yet
  const currentFileCount = mediaItems.value.length
  if (newFiles.length > currentFileCount) {
    for (let i = currentFileCount; i < newFiles.length; i++) {
      mediaItems.value.push({
        file: newFiles[i],
        name: newFiles[i].name,
        alt: '',
        title: '',
        is_free_license: false,
        collections: defaultCollectionId.value ? [defaultCollectionId.value] : [],
        isPrimary: parent.value !== null && mediaItems.value.length === 0, // First file is primary by default
        status: 'pending',
      })
    }
    // Auto-select first item if nothing selected
    if (selectedIndex.value === null && mediaItems.value.length > 0) {
      selectedIndex.value = 0
    }
  } else if (newFiles.length < currentFileCount) {
    // Files were removed — rebuild mediaItems to match
    mediaItems.value = mediaItems.value.filter((item: BulkMediaItem) => newFiles.includes(item.file))
    // Fix selectedIndex
    if (selectedIndex.value !== null && selectedIndex.value >= mediaItems.value.length) {
      selectedIndex.value = mediaItems.value.length > 0 ? mediaItems.value.length - 1 : null
    }
    // Ensure at least one is primary if parent
    if (parent.value && mediaItems.value.length > 0 && !mediaItems.value.some((i: BulkMediaItem) => i.isPrimary)) {
      mediaItems.value[0].isPrimary = true
    }
  }
}, { deep: true })

// Legacy single file watch
watch(file, (newFile) => {
  if (newFile) {
    media.value.name = newFile.name
  }
})

const selectedItem = computed(() => {
  if (selectedIndex.value === null || selectedIndex.value >= mediaItems.value.length) return null
  return mediaItems.value[selectedIndex.value]
})

const pendingCount = computed(() => mediaItems.value.filter((i: BulkMediaItem) => i.status === 'pending').length)
const doneCount = computed(() => mediaItems.value.filter((i: BulkMediaItem) => i.status === 'done').length)
const errorCount = computed(() => mediaItems.value.filter((i: BulkMediaItem) => i.status === 'error').length)
const allDone = computed(() => mediaItems.value.length > 0 && pendingCount.value === 0 && !uploading.value)
const itemStatuses = computed(() => mediaItems.value.map((i: BulkMediaItem) => i.status))
const primaryIndex = computed(() => mediaItems.value.findIndex((i: BulkMediaItem) => i.isPrimary))
const successMessage = ref<string | null>(null)

function updateItem(index: number, updated: BulkMediaItem) {
  mediaItems.value[index] = updated
}

function setPrimary(index: number) {
  mediaItems.value.forEach((item: BulkMediaItem, i: number) => {
    item.isPrimary = i === index
  })
}

function onSelect(index: number) {
  selectedIndex.value = index
}

// Client-side validation
function validateItem(item: BulkMediaItem): string | null {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png']
  if (parent.value?.type === 'icon') validTypes.push('image/svg+xml')

  if (!validTypes.includes(item.file.type)) {
    return `Ungültiger Dateityp: ${item.file.type}. Erlaubt: ${validTypes.join(', ')}`
  }
  if (item.file.size > 5 * 1024 * 1024) {
    return `Datei zu groß: ${(item.file.size / (1024*1024)).toFixed(1)} MB (max. 5 MB)`
  }
  if (!item.name.trim()) {
    return 'Name ist erforderlich'
  }
  return null
}

async function uploadAll() {
  uploading.value = true
  successMessage.value = null

  // Only count items that still need uploading (pending), skip done/error
  const itemsToUpload = mediaItems.value.filter((i: BulkMediaItem) => i.status === 'pending')
  uploadProgress.value = { done: 0, total: itemsToUpload.length }

  const uploadedMedia: any[] = []
  let primaryResponse: any = null

  for (let i = 0; i < mediaItems.value.length; i++) {
    const item = mediaItems.value[i]
    // Skip items that are already done or currently errored from a previous run
    if (item.status === 'done') continue
    if (item.status !== 'pending') continue

    // Validate
    const error = validateItem(item)
    if (error) {
      item.status = 'error'
      item.error = error
      uploadProgress.value.done++
      continue
    }

    item.status = 'uploading'

    try {
      let data: any = {
        file: item.file,
        name: item.name,
        alt: item.alt,
        title: item.title,
        is_free_license: item.is_free_license,
        collections: item.collections,
      }

      // Only attach to parent for the primary item
      if (parent.value && item.isPrimary) {
        switch (parent.value.type) {
          case 'event':
            data.event_id = parent.value.id
            data.channel = parent.value.channel
            break
          case 'location':
            data.location_id = parent.value.id
            data.channel = parent.value.channel
            break
          case 'logo':
            data.logo_id = parent.value.id
            break
          case 'icon':
            data.icon_id = parent.value.id
            break
        }
      }

      const response = await axios.post(`/api/media/upload`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      item.status = 'done'
      uploadedMedia.push(response.data.media)

      if (item.isPrimary) {
        primaryResponse = response.data
      }
    } catch (error: any) {
      item.status = 'error'
      item.error = error.response?.data?.message || error.response?.data?.errors?.file?.[0] || 'Fehler beim Hochladen'
    }

    uploadProgress.value.done++
  }

  uploading.value = false

  // Only emit (and allow modal to close) if there are NO errors
  if (errorCount.value === 0) {
    successMessage.value = `${doneCount.value} ${doneCount.value === 1 ? 'Bild' : 'Bilder'} erfolgreich hochgeladen!`

    // Show success message briefly, then emit to close modal
    setTimeout(() => {
      if (primaryResponse) {
        emit('created', primaryResponse)
      } else if (uploadedMedia.length > 0) {
        emit('created', { media: uploadedMedia[0], allMedia: uploadedMedia })
      }
    }, 1500)
  }
  // If there are errors, modal stays open — user can fix and retry
}

// Allow reset only if no files present or all uploads completed successfully
const canReset = computed(() => mediaItems.value.length === 0 || allDone.value)

function reset() {
  files.value = []
  mediaItems.value = []
  selectedIndex.value = null
  uploadProgress.value = { done: 0, total: 0 }
  successMessage.value = null
}

defineExpose({ reset, canReset })
</script>

<template>
  <div class="flex flex-col-reverse lg:flex-row justify-between">
    <!-- File area (left/main) -->
    <div class="w-full" :class="{ 'lg:w-9/12': selectedItem !== null }">
      <FileUpload
        v-model:files="files"
        :multiple="true"
        :selected-index="selectedIndex"
        :item-statuses="itemStatuses"
        :primary-index="parent ? primaryIndex : null"
        @select="onSelect"
        @set-primary="setPrimary"
      />

      <!-- Success message -->
      <div v-if="successMessage" class="alert alert-success mt-3 py-3">
        <svg class="h-5 w-5 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" /></svg>
        <span>{{ successMessage }}</span>
      </div>

      <!-- Progress & Actions -->
      <div v-if="mediaItems.length > 0" class="mt-3 flex items-center justify-between flex-wrap gap-2">
        <div class="flex items-center gap-3 text-sm">
          <span v-if="uploading">
            <span class="loading loading-spinner loading-xs"></span>
            {{ uploadProgress.done }}/{{ uploadProgress.total }} hochgeladen...
          </span>
          <span v-else-if="allDone" class="text-success flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Alle hochgeladen ({{ doneCount }} erfolgreich<span v-if="errorCount">, {{ errorCount }} fehlgeschlagen</span>)
          </span>
          <span v-else class="opacity-70">
            {{ mediaItems.length }} {{ mediaItems.length === 1 ? 'Datei' : 'Dateien' }} ausgewählt
          </span>
        </div>
        <div class="flex gap-2">
          <button v-if="allDone" class="btn btn-sm btn-outline" @click.prevent="reset">
            Zurücksetzen
          </button>
          <button
            v-if="pendingCount > 0"
            class="btn btn-sm btn-success text-white"
            :disabled="uploading || pendingCount === 0"
            @click.prevent="uploadAll"
          >
            <svg v-if="!uploading" class="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M9,16V10H5L12,3L19,10H15V16H9M5,20V18H19V20H5Z" /></svg>
            <span v-if="uploading" class="loading loading-spinner loading-xs mr-1"></span>
            {{ pendingCount === 1 ? 'Hochladen' : `Alle ${pendingCount} hochladen` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Metadata panel (right) -->
    <div v-if="selectedItem && collectionsLoaded" class="w-full lg:w-3/12 mb-4 lg:mb-0">
      <BulkUploadForm
        :item="selectedItem"
        :collections="collections"
        :has-parent="parent !== null"
        @update="(updated) => updateItem(selectedIndex!, updated)"
        @set-primary="setPrimary(selectedIndex!)"
      />
    </div>
    <div v-else-if="selectedItem && !collectionsLoaded" class="w-full lg:w-3/12 mb-4 lg:mb-0 flex items-center justify-center">
      <span class="loading loading-spinner loading-lg"></span>
    </div>
  </div>
</template>
